// Mukti Hospital ERP - Pathology Sync Engine (Phase 2c)
// NEW FILE: src/services/pathologySyncEngine.js
//
// Watches for the network coming back and pushes any queued offline
// bills to POST /api/pathology-sync/push, one at a time, in order.
//
// Design choices for safety:
//   - Heartbeat check: navigator.onLine is unreliable (Wi-Fi can be on
//     while the server is down), so before syncing we ping the API.
//   - Sequential: bills are pushed one by one so the server assigns
//     bill numbers cleanly and we can stop on the first hard failure.
//   - Idempotent: each bill carries a clientId; the server returns the
//     existing bill instead of duplicating, so re-running is safe.
//   - A simple in-memory lock prevents two syncs running at once.

import axios from 'axios';
import { getPendingBills, markBillSynced, markBillFailed, getAllPending, markTestSynced, markAgentSynced, markCategorySynced, rememberAgentIdMapping, resolveAgentId } from './pathologyOutbox';

// Reuse the same API base the rest of the app uses.
const API_BASE = 'https://api.muktihospital.com/api';
const SYNC_URL = `${API_BASE}/pathology-sync/push`;
const TEST_SYNC_URL = `${API_BASE}/pathology-test-sync/push`;
const AGENT_SYNC_URL = `${API_BASE}/pathology-agent-sync/push`;
const CATEGORY_SYNC_URL = `${API_BASE}/pathology-category-sync/push`;
// Heartbeat: hit the existing bills endpoint cheaply (limit=1) to confirm
// the server is actually reachable. No new API route needed.
const HEARTBEAT_URL = `${API_BASE}/pathology-bill?page=1&limit=1`;

// The API key is passed in by the calling component (which already has it).
// We keep a module-level copy once it is provided.
let API_KEY = '';

let isSyncing = false;
let listeners = [];

/** Subscribe to sync status changes. Returns an unsubscribe function. */
export const onSyncStatusChange = (cb) => {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
};

const notify = (status) => {
  listeners.forEach((l) => {
    try {
      l(status);
    } catch (_) {}
  });
};

/** Quick check: is the API actually reachable right now? */
const isServerReachable = async () => {
  if (!navigator.onLine) return false;
  try {
    await axios.get(HEARTBEAT_URL, {
      headers: { 'x-api-key': API_KEY },
      timeout: 5000,
    });
    return true;
  } catch (err) {
    // 401/403 means the server IS up (just auth) -> reachable.
    if (err.response && [401, 403].includes(err.response.status)) return true;
    return false;
  }
};

/**
 * Try to sync all pending offline bills.
 * Safe to call often (on reconnect, on interval, on manual click).
 * @param {object} opts { apiKey } optional override for the API key
 * @returns {Promise<{synced:number, failed:number, skipped:boolean}>}
 */
export const syncPendingBills = async (opts = {}) => {
  if (isSyncing) return { synced: 0, failed: 0, skipped: true };

  if (opts.apiKey) API_KEY = opts.apiKey;
  const apiKey = API_KEY;
  const pending = await getAllPending();
  if (pending.length === 0) {
    notify({ syncing: false, pending: 0 });
    return { synced: 0, failed: 0, skipped: false };
  }

  const reachable = await isServerReachable();
  if (!reachable) {
    notify({ syncing: false, pending: pending.length, offline: true });
    return { synced: 0, failed: 0, skipped: true };
  }

  isSyncing = true;
  notify({ syncing: true, pending: pending.length });

  let synced = 0;
  let failed = 0;

  for (const item of pending) {
    const itemType = item.type || 'bill';
    let itemUrl;
    let itemLabel;
    if (itemType === 'test') {
      itemUrl = TEST_SYNC_URL;
      itemLabel = 'test';
    } else if (itemType === 'agent') {
      itemUrl = AGENT_SYNC_URL;
      itemLabel = 'agent';
    } else if (itemType === 'category') {
      itemUrl = CATEGORY_SYNC_URL;
      itemLabel = 'category';
    } else {
      itemUrl = SYNC_URL;
      itemLabel = 'bill';
    }
    // For bills only: if the bill carries an offline agentId
    let payloadToSend = item.payload;
    if (itemType === 'bill') {
      const offlineAgentId = item.payload?.agentId;
      if (
        offlineAgentId &&
        typeof offlineAgentId === 'string' &&
        offlineAgentId.startsWith('offline-')
      ) {
        const realAgentId = resolveAgentId(offlineAgentId);
        payloadToSend = { ...item.payload, agentId: realAgentId || null };
      }
    }
    try {
      const res = await axios.post(itemUrl, payloadToSend, {
        headers: { 'x-api-key': apiKey },
        timeout: 20000,
      });
      if (itemType === 'test') {
        await markTestSynced(item.id);
      } else if (itemType === 'agent') {
        // Save offline->real id mapping so any bill referencing this agent
        // by its offline placeholder can be translated when it syncs.
        const realAgentId = res.data?.agent?.id;
        if (realAgentId) {
          rememberAgentIdMapping(item.clientId, realAgentId);
        }
        await markAgentSynced(item.id);
      } else if (itemType === 'category') {
        await markCategorySynced(item.id);
      } else {
        const realBillNumber = res.data?.bill?.billNumber || null;
        await markBillSynced(item.id, item.clientId, realBillNumber);
      }
      synced++;
      notify({ syncing: true, pending: pending.length - synced });
    } catch (err) {
      const serverMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      console.error(
        `[SyncEngine] Failed to sync ${itemLabel} ${item.clientId} -> status ${err.response?.status || "?"}: ${serverMsg}`,
        err.response?.data || err
      );
      failed++;
      // Network dropped mid-sync -> stop and try again later (don't count it).
      if (err.code === 'ERR_NETWORK' || !navigator.onLine) {
        break;
      }
      // Server-side error (validation/data problem): count this attempt.
      // After MAX_SYNC_ATTEMPTS the item is marked 'failed' and skipped so it
      // doesn't retry forever or block the rest of the queue.
      const gaveUp = await markBillFailed(item.id);
      if (gaveUp) {
        console.error(
          `[SyncEngine] ${itemLabel} ${item.clientId} permanently failed after multiple attempts. Marked as failed.`
        );
      }
    }
  }

  isSyncing = false;
  const remaining = await getAllPending();
  notify({ syncing: false, pending: remaining.length, justSynced: synced });

  return { synced, failed, skipped: false };
};

/**
 * Start automatic syncing: runs when the browser regains connectivity,
 * and also on a periodic timer as a safety net.
 * @returns {function} stop function to remove listeners/timer
 */
export const startAutoSync = (opts = {}) => {
  if (opts.apiKey) API_KEY = opts.apiKey;

  const handleOnline = () => {
    syncPendingBills(opts);
  };

  window.addEventListener('online', handleOnline);

  // Safety-net poll every 30s (covers "Wi-Fi on but server was down").
  const interval = setInterval(() => {
    syncPendingBills(opts);
  }, 30000);

  // Try once right away in case there are leftovers from a previous session.
  syncPendingBills(opts);

  return () => {
    window.removeEventListener('online', handleOnline);
    clearInterval(interval);
  };
};
